import { Injectable } from '@angular/core';
import {dayjs} from '@karve.it/core';
import { clone } from 'lodash';

import { environment } from '../../environments/environment';
import { BaseZoneFragment } from '../../generated/graphql.generated';

import { AdvancedPeriodOptions, Period, periodOptions, PeriodOptions } from '../reports/reports.constants';
import { BrandingService } from '../services/branding.service';

@Injectable({
  providedIn: 'root'
})
export class PeriodService {

  zone: BaseZoneFragment;

  constructor(
    private branding: BrandingService,
  ) {
    this.branding.currentZone().subscribe((z) => {
      this.zone = z;
    });
  }

  getUnixPeriod(
    period: AdvancedPeriodOptions,
    timezone?: string,
  ): Period {
    const now = timezone ? dayjs.tz(undefined, timezone) : dayjs();
    const eod = now.endOf('day').unix();

    const beginningOfTime = this.zone?.createdAt || now.subtract(30, 'years').unix();
    switch (period) {
      default:
      case 'Custom':
        return { start: now.startOf('day').unix(), end: eod};
      case 'Today': return {
        start: now.startOf('day').unix(),
        end: eod,
      };
      case 'Tomorrow': return {
        start: now.startOf('day').add(1, 'day').unix(),
        end: now.endOf('day').add(1, 'day').unix(),
      };
      case 'Yesterday': return {
        start: now.startOf('day').subtract(1, 'day').unix(),
        end: now.endOf('day').subtract(1, 'day').unix(),
      };
      case 'Next 24 hours': return {
        start: now.unix(),
        end: now.add(1, 'day').unix(),
      };
      case 'Last 24 hours': return {
        start: now.subtract(1, 'day').unix(),
        end: now.unix(),
      };

      case 'Last Week': return {
        start: now.subtract(7, 'days').startOf('week').unix(),
        end: now.subtract(7, 'days').endOf('week').unix(),
      };
      case 'This Week': return {
        start: now.startOf('week').unix(),
        end: now.endOf('week').unix(),
      };
      case 'Next Week': return {
        start: now.add(7, 'days').startOf('week').unix(),
        end: now.add(7, 'days').endOf('week').unix(),
      };
      case 'WTD': return {
        start: now.startOf('week').unix(),
        end: eod,
      };
      case 'Last 7 days': return {
        start: now.subtract(7, 'days').startOf('day').unix(),
        end: eod,
      };
      case 'Next 7 days': return {
        start: now.startOf('day').unix(),
        end: now.add(7, 'days').endOf('day').unix(),
      };
      case 'Last 14 days': return {
        start: now.subtract(14, 'days').startOf('day').unix(),
        end: eod,
      };

      case 'Last Month': return {
        start: now.subtract(1, 'month').startOf('month').unix(),
        end: now.subtract(1, 'month').endOf('month').unix(),
      };
      case 'This Month': return {
        start: now.startOf('month').unix(),
        end: now.endOf('month').unix(),
      };
      case 'Next Month': return {
        start: now.add(1, 'month').startOf('month').unix(),
        end: now.add(1, 'month').endOf('month').unix(),
      };
      case 'Month After Next': return {
        start: now.add(2, 'month').startOf('month').unix(),
        end: now.add(2, 'month').endOf('month').unix(),
      };
      case 'MTD': return {
        start: now.startOf('month').unix(),
        end: eod,
      };
      case 'MTD Last Year': return {
        start: now.subtract(1, 'year').startOf('month').unix(),
        end: now.subtract(1, 'year').endOf('day').unix(),
      };
      case 'Last 30 days': return {
        start: now.subtract(30, 'days').startOf('day').unix(),
        end: eod,
      };

      case 'Last Quarter': return {
        start: now.subtract(1, 'quarter').startOf('quarter').unix(),
        end: now.subtract(1, 'quarter').endOf('quarter').unix(),
      };
      case 'This Quarter': return {
        start: now.startOf('quarter').unix(),
        end: now.endOf('quarter').unix(),
      };
      case 'Next Quarter': return {
        start: now.add(1, 'quarter').startOf('quarter').unix(),
        end: now.add(1, 'quarter').endOf('quarter').unix(),
      };
      case 'QTD': return {
        start: now.startOf('quarter').unix(),
        end: eod,
      };
      case 'Last 90 days': return {
        start: now.subtract(90, 'days').startOf('day').unix(),
        end: eod,
      };

      case 'Last Year': return {
        start: now.subtract(1, 'year').startOf('year').unix(),
        end: now.subtract(1, 'year').endOf('year').unix(),
      };
      case 'This Year': return {
        start: now.startOf('year').unix(),
        end: now.endOf('year').unix(),
      };
      case 'Next Year': return {
        start: now.add(1, 'year').startOf('year').unix(),
        end: now.add(1, 'year').endOf('year').unix(),
      };
      case 'YTD': return {
        start: now.startOf('year').unix(),
        end: eod,
      };
      case 'Last 365 days': return {
        start: now.subtract(365, 'days').startOf('day').unix(),
        end: eod,
      };

      case 'Past':
        return {
          start: beginningOfTime,
          end: now.unix(),
        };
      case 'Future':
        return {
          start: now.unix(),
          end: now.add(30, 'years').unix(),
        };

      case 'All Time':
        return {
          start: beginningOfTime,
          end: now.add(30, 'years').unix(),
        };
    }
  }

  getPeriodAsDateArray(period: AdvancedPeriodOptions) {
    const unixPeriod = this.getUnixPeriod(period);
    const start = new Date(unixPeriod.start * 1000);
    const end = new Date(unixPeriod.end * 1000);
    return [ start, end ];
  }

  offsetUnixPeriod(period: Period, offset = 0) {
    const diff = period.end - period.start;

    return {
      start: period.start + diff * offset,
      end: period.end + diff * offset,
    };

  }

  generateKey(period: Period) {
    return `${ period.start }.${ period.end }`;
  }

  getPeriodDropdownCount(): {
    [ key in string ]: number;
  } {
    const strCounts = localStorage.getItem(environment.lskeys.periodDropdownCount) || '{}';
    try {
      return JSON.parse(strCounts);
    } catch (_err) {
      return {};
    }
  }

  /**
   * Update counts in local storage for periods most used
   */
  updatePeriodLocalStorage(period: PeriodOptions) {
    const counts = this.getPeriodDropdownCount();
    counts[period] = counts[period] || 0;
    counts[period]++;
    // console.log(`Updating period counts in localstorage ${ period }`, counts);
    localStorage.setItem(environment.lskeys.periodDropdownCount, JSON.stringify(counts));
  }

  getPeriodNameValues(opts?: {
    defaultPeriod?: AdvancedPeriodOptions;
    includeCustom?: boolean;
    sortTop?: number;
    includeSinceLastRun?: boolean;
  }) {
    let periods = clone(periodOptions) as unknown as AdvancedPeriodOptions[];
    if (opts?.includeCustom) {
      periods.push('Custom');
    }

    if (opts?.includeSinceLastRun) {
      periods.push(`Since Last Run`);
    }

    if (opts.sortTop) {
      // move top periods to top
      const topPeriods = Object.entries(this.getPeriodDropdownCount())
        .filter(([ _, count ]) => count > 0)
        .filter(( [ value ] ) => periods.includes(value as any))
        .sort(([ _a, acount ], [ _b, bcount ]) => bcount - acount)
        .slice(0, opts.sortTop)
        .map(([ value ]) => value as typeof periods[number])
      ;

      // remove top periods from periods and place them at the front, sorted
      periods = topPeriods.concat(periods.filter((p) => !topPeriods.includes(p)));
    }

    const periodNameValue = periods.map((value) => ({
      name: value as string,
      value,
    }));

    if (opts?.defaultPeriod) {
      for (const period of periodNameValue) {
        if (period.value === opts.defaultPeriod) {
          period.name = `${ period.name } (default)`;
          break;
        }
      }
    }

    return periodNameValue;
  }

}
